/**
 * Saha detayı — sahanın panoları ve tankları TEK EKRANDA.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * NEDEN LİSTELERDEN AYRI BİR EKRAN
 *
 * "Bu sahadan neden veri gelmiyor?" sorusunun cevabı üç yerdedir: sahanın
 * panosu var mı, panonun adresi doğru mu, yoklaması açık mı. Üçü üç ayrı
 * listede olsaydı kullanıcı sekme değiştirip filtre kurarak dolaşırdı.
 * Sunucu zaten üçünü tek istekte veriyor (`GET /sites/:id`).
 * ═══════════════════════════════════════════════════════════════════════════
 */
import { Link, useParams } from '@tanstack/react-router';

import { ApiError } from '../lib/api.ts';
import { Card, CardBody, CardHeader } from '../design/Card.tsx';
import { EmptyState, SkeletonList } from '../design/Feedback.tsx';
import { PageBody, PageHeader } from '../shell/AppShell.tsx';
import { PanoKarti } from './PanoListesi.tsx';
import { SahaOkumasi } from './SahaOkumasi.tsx';
import { TankKarti } from './TankListesi.tsx';
import { Button } from '../design/Button.tsx';
import { Yetki } from '../auth/Yetki.tsx';
import { useSaha } from './api.ts';

export function SahaDetay(): React.JSX.Element {
  const { sahaId } = useParams({ from: '/korumali/sahalar/$sahaId' });
  const sorgu = useSaha(Number(sahaId));

  if (sorgu.isPending) {
    return (
      <PageBody>
        <SkeletonList rows={3} />
      </PageBody>
    );
  }

  if (sorgu.isError) {
    /*
     * 404 ile "bir şeyler ters gitti" AYNI ŞEY DEĞİL. Silinmiş (ya da başka
     * firmaya ait — RLS onu da görünmez kılar) bir sahaya "tekrar dene"
     * demek kullanıcıyı boşuna uğraştırır.
     */
    const yok = sorgu.error instanceof ApiError && sorgu.error.status === 404;
    return (
      <PageBody>
        <EmptyState
          title={yok ? 'Saha bulunamadı' : 'Saha yüklenemedi'}
          description={
            yok
              ? 'Bu saha silinmiş olabilir ya da erişiminiz yok.'
              : sorgu.error instanceof Error
                ? sorgu.error.message
                : 'Bilinmeyen bir hata.'
          }
          action={
            yok ? (
              <Link to="/sahalar">
                <Button variant="secondary">Saha listesine dön</Button>
              </Link>
            ) : (
              <Button onClick={() => void sorgu.refetch()}>Tekrar dene</Button>
            )
          }
        />
      </PageBody>
    );
  }

  const { site, panels, tanks } = sorgu.data;
  const yer = [site.district, site.city].filter((v) => v !== null && v !== '').join(' / ');

  return (
    <>
      <PageHeader
        title={site.name}
        description={`${site.addr_code}${yer === '' ? '' : ` · ${yer}`}`}
        action={
          <div className="flex shrink-0 items-center gap-3">
            <Yetki gerekli="site_edit">
              <Link
                to="/sahalar/$sahaId/duzenle"
                params={{ sahaId: String(site.id) }}
                className="text-sm font-medium text-brand-700"
              >
                Düzenle
              </Link>
            </Yetki>
            <Link to="/sahalar" className="text-sm font-medium text-ink-muted">
              Listeye dön
            </Link>
          </div>
        }
      />
      <PageBody>
        <div className="flex flex-col gap-6">
          {/*
           * ÖNCE ÖLÇÜLEN, SONRA AYARLANAN. Kullanıcı bu sayfaya çoğunlukla
           * "bu sahada ne oluyor?" diye gelir; yapılandırma ayda birkaç kez
           * bakılan bir bölüm ve altta duruyor.
           */}
          <SahaOkumasi sahaId={site.id} />

          <Card>
            <CardHeader>
              <h2 className="text-sm font-semibold text-ink">Saha bilgileri</h2>
            </CardHeader>
            <CardBody>
              <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
                <Satir baslik="Tesisat/abone kodu" deger={site.addr_code} />
                <Satir baslik="Saha kodu" deger={site.site_code} />
                <Satir baslik="Adres" deger={site.address} />
                <Satir baslik="İlgili kişi" deger={site.contact_person} />
                <Satir baslik="Telefon" deger={site.contact_phone} />
                <Satir baslik="E-posta" deger={site.contact_email} />
                {/*
                 * Koordinat İKİ ONDALIKLA değil, altı ile gösteriliyor:
                 * yuvarlanmış bir koordinat haritada yüzlerce metre kayar ve
                 * saha ekibi yanlış yere gider.
                 */}
                <Satir
                  baslik="Konum"
                  deger={`${site.latitude.toFixed(6)}, ${site.longitude.toFixed(6)}`}
                />
              </dl>
            </CardBody>
          </Card>

          <section aria-labelledby="panolar-baslik">
            <div className="mb-2 flex items-center justify-between gap-3">
              <h2 id="panolar-baslik" className="text-sm font-semibold text-ink">
                Panolar ({panels.length})
              </h2>
              {/*
               * Saha ÖNCEDEN seçili gelir (`?siteId=`): kullanıcı bu sahanın
               * ekranından geldiğine göre sahayı yeniden aramak zorunda değil.
               * Parametre yalnız formu ön doldurur, yetki taşımaz [K-010].
               */}
              <Yetki gerekli="add_panel">
                <Link
                  to="/panolar/yeni"
                  search={{ siteId: site.id }}
                  className="text-sm font-medium text-brand-700"
                >
                  Pano ekle
                </Link>
              </Yetki>
            </div>
            {panels.length === 0 ? (
              <EmptyState
                title="Bu sahanın panosu yok"
                // Tahmin değil, kesinlik: panosuz sahadan veri GELMEZ.
                description="Panosu tanımlanmamış bir sahadan telemetri toplanamaz."
                action={
                  <Yetki gerekli="add_panel">
                    <Link to="/panolar/yeni" search={{ siteId: site.id }}>
                      <Button variant="secondary">Pano ekle</Button>
                    </Link>
                  </Yetki>
                }
              />
            ) : (
              <ul className="flex flex-col gap-3">
                {panels.map((pano) => (
                  <li key={pano.id}>
                    <PanoKarti pano={pano} />
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section aria-labelledby="tanklar-baslik">
            <div className="mb-2 flex items-center justify-between gap-3">
              <h2 id="tanklar-baslik" className="text-sm font-semibold text-ink">
                Tanklar ({tanks.length})
              </h2>
              <Yetki gerekli="add_tank">
                <Link
                  to="/tanklar/yeni"
                  search={{ siteId: site.id }}
                  className="text-sm font-medium text-brand-700"
                >
                  Tank ekle
                </Link>
              </Yetki>
            </div>
            {tanks.length === 0 ? (
              <EmptyState
                title="Bu sahada tank yok"
                description="Tanklar depodan bu sahaya atanabilir."
                action={
                  <Yetki gerekli="add_tank">
                    <Link to="/tanklar/yeni" search={{ siteId: site.id }}>
                      <Button variant="secondary">Tank ekle</Button>
                    </Link>
                  </Yetki>
                }
              />
            ) : (
              <ul className="flex flex-col gap-3">
                {tanks.map((tank) => (
                  <li key={tank.id}>
                    <TankKarti tank={{ ...tank, site_id: site.id, site_name: site.name }} />
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </PageBody>
    </>
  );
}

/** Boş alan HİÇ gösterilmez — "—" dizisi ekranı gürültüye boğar. */
function Satir({
  baslik,
  deger,
}: {
  readonly baslik: string;
  readonly deger: string | null;
}): React.JSX.Element | null {
  if (deger === null || deger === '') return null;
  return (
    <div className="flex min-w-0 gap-2">
      <dt className="shrink-0 text-ink-muted">{baslik}:</dt>
      <dd className="min-w-0 break-words font-medium text-ink">{deger}</dd>
    </div>
  );
}
